import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { GoogleMap, useLoadScript, Marker } from "@react-google-maps/api";
import type { Libraries } from "@react-google-maps/api";
import { useMediaQuery } from "@mui/material";
import styles from "./LocationMapModal.module.scss";
import Loader from "@/components/Loader/Loader";
import { captalizeEachWord, combineClasses } from "@/lib/utils";
import Button from "@/components/Button/Button";
import Cancel from "@/components/SVGComponents/cancel";
import Modal from "@/components/Modal/Modal";
import type { PlaceSuggestion, Props } from "@/types/events";
import { cancel, confirm } from "@/services/api/modal";
import {
  GOOGLE_MAPS_CONFIG,
  MAP_CONFIG,
  ERROR_MESSAGES,
  API_FIELD_MAPPINGS,
} from "./mapModal.constants";

const MOBILE_BREAKPOINT = "(max-width: 640px)";

const formatCoordinates = (position: google.maps.LatLngLiteral) =>
  `${position.lat.toFixed(5)}, ${position.lng.toFixed(5)}`;

const LocationMapModal = ({
  isOpen,
  onClose,
  onSelectLocation,
  initialAddress = "",
}: Props) => {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    libraries: [...MAP_CONFIG.LIBRARIES] as Libraries,
  });

  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const mapContainerStyle = useMemo(
    () => ({
      width: "100%",
      height: isMobile ? "100%" : MAP_CONFIG.CONTAINER_HEIGHT,
    }),
    [isMobile],
  );

  const [markerPosition, setMarkerPosition] = useState<google.maps.LatLngLiteral>(
    MAP_CONFIG.DEFAULT_CENTER,
  );
  const [selectedPlaceName, setSelectedPlaceName] = useState<string | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isResolvingAddress, setIsResolvingAddress] = useState(false);

  const mapRef = useRef<google.maps.Map | null>(null);
  const markerPositionRef = useRef(markerPosition);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  markerPositionRef.current = markerPosition;

  const API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  const onMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSelectedPlaceName(null);
    setSuggestions([]);
    setShowDropdown(false);
    setIsResolvingAddress(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
  };

  const resolveAddressFromMarker = useCallback(
    async (position: google.maps.LatLngLiteral) => {
      if (!window.google?.maps) {
        setSelectedPlaceName(formatCoordinates(position));
        return;
      }

      setIsResolvingAddress(true);
      try {
        const geocoder = new window.google.maps.Geocoder();
        const response = await geocoder.geocode({ location: position });
        const formattedAddress = response.results?.[0]?.formatted_address;
        setSelectedPlaceName(
          formattedAddress || formatCoordinates(position),
        );
      } catch (error) {
        console.error(ERROR_MESSAGES.PLACE_DETAILS_ERROR, error);
        setSelectedPlaceName(formatCoordinates(position));
      } finally {
        setIsResolvingAddress(false);
      }
    },
    [],
  );

  const handleMapClick = useCallback(
    (e: google.maps.MapMouseEvent) => {
      if (!e.latLng) return;
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      const position = { lat, lng };
      setMarkerPosition(position);
      setSelectedPlaceName(null);
      void resolveAddressFromMarker(position);
    },
    [resolveAddressFromMarker],
  );

  /**
   * Fetches place suggestions from Google Places API
   * @param input - Search query string
   */
  const fetchSuggestions = async (input: string) => {
    if (!input.trim()) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(
        GOOGLE_MAPS_CONFIG.AUTOCOMPLETE_API,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Goog-Api-Key": API_KEY,
          },
          body: JSON.stringify({ input }),
        },
      );

      const data = await response.json();

      const results: PlaceSuggestion[] =
        data.suggestions
          ?.filter((s: any) => API_FIELD_MAPPINGS.SUGGESTION_FILTER(s))
          .map((s: any) => ({
            placeId: API_FIELD_MAPPINGS.PLACE_ID(s),
            description: API_FIELD_MAPPINGS.DESCRIPTION(s),
          })) || [];

      setSuggestions(results);
      setShowDropdown(results.length > 0);
    } catch (error) {
      console.error(ERROR_MESSAGES.AUTOCOMPLETE_ERROR, error);
      setSuggestions([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!value.trim()) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    debounceRef.current = setTimeout(() => {
      fetchSuggestions(value);
    }, GOOGLE_MAPS_CONFIG.DEBOUNCE_DELAY_MS);
  };

  /**
   * @param suggestion - Selected place suggestion
   */
  const handleSelectSuggestion = async (suggestion: PlaceSuggestion) => {
    setSearchQuery(suggestion.description);
    setSelectedPlaceName(suggestion.description);
    setShowDropdown(false);
    setSuggestions([]);

    setIsResolvingAddress(true);
    try {
      const response = await fetch(
        `${GOOGLE_MAPS_CONFIG.PLACE_DETAILS_API}/${suggestion.placeId}`,
        {
          headers: {
            "X-Goog-Api-Key": API_KEY,
            "X-Goog-FieldMask":
              "location,formattedAddress,displayName",
          },
        },
      );

      const data = await response.json();
      const lat = API_FIELD_MAPPINGS.LATITUDE(data);
      const lng = API_FIELD_MAPPINGS.LONGITUDE(data);
      const formattedAddress = API_FIELD_MAPPINGS.FORMATTED_ADDRESS(data);

      if (formattedAddress) {
        setSelectedPlaceName(formattedAddress);
        setSearchQuery(formattedAddress);
      }

      if (lat == null || lng == null) {
        console.error(ERROR_MESSAGES.NO_LOCATION_DATA);
        return;
      }

      const newPosition = { lat, lng };
      setMarkerPosition(newPosition);

      if (mapRef.current) {
        mapRef.current.panTo(newPosition);
        mapRef.current.setZoom(GOOGLE_MAPS_CONFIG.MARKER_ZOOM);
      }
    } catch (error) {
      console.error(ERROR_MESSAGES.PLACE_DETAILS_ERROR, error);
    } finally {
      setIsResolvingAddress(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  useEffect(() => {
    if (!isOpen || !isLoaded) return;

    const trimmedInitial = initialAddress.trim();

    if (trimmedInitial) {
      setSelectedPlaceName(trimmedInitial);
      setSearchQuery(trimmedInitial);

      if (!window.google?.maps) return;

      const geocoder = new window.google.maps.Geocoder();
      (async () => {
        try {
          const response = await geocoder.geocode({ address: trimmedInitial });
          const location = response.results?.[0]?.geometry?.location;
          const lat = location?.lat?.();
          const lng = location?.lng?.();
          if (lat != null && lng != null) {
            setMarkerPosition({ lat, lng });
          }
        } catch {
          // Keep address preview even if we can't place the marker.
        }
      })();
      return;
    }

    void resolveAddressFromMarker(markerPositionRef.current);
  }, [initialAddress, isLoaded, isOpen, resolveAddressFromMarker]);

  /**
   * Confirms location selection and geocodes if needed
   */
  const handleConfirm = async () => {
    let formattedAddress = selectedPlaceName;

    if (!formattedAddress) {
      const geocoder = new window.google.maps.Geocoder();
      const response = await geocoder.geocode({ location: markerPosition });
      formattedAddress =
        response.results[0]?.formatted_address || "Selected Location";
    }

    onSelectLocation({
      name: formattedAddress,
      lat: markerPosition.lat,
      lng: markerPosition.lng,
    });

    clearSearch();
    onClose();
  };

  const handleCancel = () => {
    clearSearch();
    onClose();
  };

  const displaySelectedLocation =
    selectedPlaceName?.trim() ||
    searchQuery.trim() ||
    "Click the map or search to select a location";

  if (!isLoaded) {
    return isOpen ? (
      <Modal
        modalOpen={isOpen}
        closeModal={onClose}
        heading="Select Location"
        doNotClose={false}
        className={styles.locationModal ?? ""}
        bodyClassName={styles.locationModalBody ?? ""}
        fullScreenOnMobile
      >
        <div className={styles.loadingContainer}>
          <Loader fullscreen={false} size={84} />
        </div>
      </Modal>
    ) : null;
  }

  return (
    <Modal
      modalOpen={isOpen}
      closeModal={onClose}
      heading="Select Location"
      doNotClose={false}
      className={styles.locationModal ?? ""}
      bodyClassName={styles.locationModalBody ?? ""}
      fullScreenOnMobile
    >
      <div className={styles.modalLayout}>
        {/* Search Bar + Dropdown */}
        <div ref={dropdownRef} className={styles.topPanel}>
          <div className={styles.searchInputWrapper}>
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search location..."
              className={styles.searchInput}
            />

            {/* Clear button */}
            {searchQuery && (
              <button
                onClick={clearSearch}
                aria-label="Clear search"
                className={styles.clearButton}
              >
                <Cancel />
              </button>
            )}
          </div>

          {isSearching && (
            <div className={styles.dropdownContent}>Searching...</div>
          )}

          {showDropdown && suggestions.length > 0 && (
            <ul className={styles.suggestionList}>
              {suggestions.map((s) => (
                <li
                  key={s.placeId}
                  onClick={() => handleSelectSuggestion(s)}
                  className={styles.suggestionItem}
                >
                  {s.description}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className={styles.mapContainer}>
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={markerPosition}
            zoom={GOOGLE_MAPS_CONFIG.DEFAULT_ZOOM}
            onClick={handleMapClick}
            onLoad={onMapLoad}
          >
            <Marker
              key={`${markerPosition.lat}-${markerPosition.lng}`}
              position={markerPosition}
            />
          </GoogleMap>
        </div>

        <div className={styles.modalFooter}>
          <div className={styles.selectedLocation}>
            <span className={styles.selectedLocationLabel}>
              Selected location
            </span>

            {isResolvingAddress ? (
              <div className={styles.resolvingAddress}>
                <span aria-hidden className={styles.resolvingSpinner} />
                <span className={styles.resolvingAddressText}>
                  Fetching address...
                </span>
              </div>
            ) : (
              <p className={styles.selectedLocationValue}>
                {displaySelectedLocation}
              </p>
            )}
          </div>

          <div className={styles.actions}>
            <Button
              type="button"
              className={combineClasses(styles.actionButton, styles.cancelButton)}
              onClick={handleCancel}
              outlined
            >
              {captalizeEachWord(cancel)}
            </Button>

            <Button
              type="submit"
              className={combineClasses(styles.actionButton, styles.submitButton)}
              disabled={isResolvingAddress}
              onClick={handleConfirm}
            >
              {captalizeEachWord(confirm)}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default LocationMapModal;
